/**
 * Product Community — Reviews + Questions & Answers
 *
 * <product-community-tabs>     Accessible tabs, deep links, theme editor support, "Ask a question" dialog.
 * <product-community-reviews>  Okendo Storefront REST API reviews rendered as on-brand cards,
 *                              with topic filters, pagination, read more, and a native-widget fallback.
 *
 * Okendo Storefront REST API: https://api.okendo.io/v1/stores/{okendo_user_id}
 * All review content from the API is written with textContent (never innerHTML).
 */
(() => {
  const OKENDO_API_ORIGIN = 'https://api.okendo.io/v1';
  const OKENDO_API_VERSION = '2025-02-01';
  const MAXIMUM_AUTOMATIC_PAGE_REQUESTS = 3;

  /* ------------------------------------------------------------------------
     Tabs
     ------------------------------------------------------------------------ */

  class ProductCommunityTabs extends HTMLElement {
    constructor() {
      super();
      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleTabKeydown = this.handleTabKeydown.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleAskDialogOpen = this.handleAskDialogOpen.bind(this);
      this.handleAskDialogClose = this.handleAskDialogClose.bind(this);
      this.handleAskDialogBackdropClick = this.handleAskDialogBackdropClick.bind(this);
    }

    connectedCallback() {
      this.tabList = this.querySelector('[data-tab-list]');
      this.tabButtons = Array.from(this.querySelectorAll('[data-tab-button]'));
      this.tabPanels = Array.from(this.querySelectorAll('[data-tab-panel]'));

      this.tabButtons.forEach((tabButton) => tabButton.addEventListener('click', this.handleTabClick));
      this.tabList?.addEventListener('keydown', this.handleTabKeydown);
      window.addEventListener('hashchange', this.handleHashChange);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
      }

      this.setupAskDialog();
      this.openTabFromLocationHash();
    }

    disconnectedCallback() {
      this.tabButtons.forEach((tabButton) => tabButton.removeEventListener('click', this.handleTabClick));
      this.tabList?.removeEventListener('keydown', this.handleTabKeydown);
      window.removeEventListener('hashchange', this.handleHashChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.askDialogOpenButton?.removeEventListener('click', this.handleAskDialogOpen);
      this.askDialogCloseButton?.removeEventListener('click', this.handleAskDialogClose);
      this.askDialog?.removeEventListener('click', this.handleAskDialogBackdropClick);
    }

    handleTabClick(event) {
      this.activateTab(event.currentTarget.dataset.tabButton);
    }

    handleTabKeydown(event) {
      const currentIndex = this.tabButtons.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      const lastIndex = this.tabButtons.length - 1;
      const keyToIndex = {
        ArrowRight: currentIndex === lastIndex ? 0 : currentIndex + 1,
        ArrowLeft: currentIndex === 0 ? lastIndex : currentIndex - 1,
        Home: 0,
        End: lastIndex,
      };

      if (!(event.key in keyToIndex)) return;

      event.preventDefault();
      const nextTabButton = this.tabButtons[keyToIndex[event.key]];
      this.activateTab(nextTabButton.dataset.tabButton, { focusTab: true });
    }

    handleHashChange() {
      this.openTabFromLocationHash();
    }

    handleBlockSelect(event) {
      if (event.detail?.sectionId !== this.dataset.sectionId) return;
      const selectedPanel = event.target.closest('[data-tab-panel]');
      if (selectedPanel && this.contains(selectedPanel)) {
        this.activateTab(selectedPanel.dataset.tabPanel);
      }
    }

    openTabFromLocationHash() {
      const locationHash = window.location.hash.slice(1);
      if (!locationHash) return;

      if (this.dataset.askFormId && locationHash === this.dataset.askFormId) {
        this.activateTab('questions');
        this.openAskDialog();
        return;
      }

      const targetElement = document.getElementById(decodeURIComponent(locationHash));
      if (!targetElement || !this.contains(targetElement)) return;

      const targetPanel = targetElement.closest('[data-tab-panel]');
      const targetTabButton = targetElement.closest('[data-tab-button]');
      const tabName = targetPanel?.dataset.tabPanel || targetTabButton?.dataset.tabButton;
      if (tabName) this.activateTab(tabName);
    }

    activateTab(tabName, { focusTab = false } = {}) {
      if (!tabName) return;

      this.tabButtons.forEach((tabButton) => {
        const isActive = tabButton.dataset.tabButton === tabName;
        tabButton.setAttribute('aria-selected', String(isActive));
        tabButton.setAttribute('tabindex', isActive ? '0' : '-1');
        if (isActive && focusTab) tabButton.focus();
      });

      this.tabPanels.forEach((tabPanel) => {
        tabPanel.hidden = tabPanel.dataset.tabPanel !== tabName;
      });

      // Third-party widgets (e.g. Okendo) measure layout on resize; nudge them after a hidden panel appears.
      window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));

      this.dispatchEvent(
        new CustomEvent('product-community:tab-change', { bubbles: true, detail: { tabName } })
      );
    }

    /* ---- Ask a question dialog ---- */

    setupAskDialog() {
      this.askDialog = this.querySelector('[data-ask-question-dialog]');
      if (!this.askDialog) return;

      this.askDialogOpenButton = this.querySelector('[data-ask-question-open]');
      this.askDialogCloseButton = this.askDialog.querySelector('[data-ask-question-close]');

      this.askDialogOpenButton?.addEventListener('click', this.handleAskDialogOpen);
      this.askDialogCloseButton?.addEventListener('click', this.handleAskDialogClose);
      this.askDialog.addEventListener('click', this.handleAskDialogBackdropClick);

      // After a submission Shopify reloads the page; reopen the dialog so the status message is visible.
      if (this.askDialog.querySelector('[data-ask-question-status]')) {
        this.activateTab('questions');
        this.openAskDialog();
      }
    }

    handleAskDialogOpen() {
      this.openAskDialog();
    }

    handleAskDialogClose() {
      this.closeAskDialog();
    }

    handleAskDialogBackdropClick(event) {
      if (event.target === this.askDialog) this.closeAskDialog();
    }

    openAskDialog() {
      if (!this.askDialog || this.askDialog.open) return;

      if (typeof this.askDialog.showModal === 'function') {
        this.askDialog.showModal();
      } else {
        this.askDialog.setAttribute('open', '');
      }

      const firstField = this.askDialog.querySelector('input:not([type="hidden"]), textarea');
      firstField?.focus();
    }

    closeAskDialog() {
      if (!this.askDialog) return;

      if (typeof this.askDialog.close === 'function') {
        this.askDialog.close();
      } else {
        this.askDialog.removeAttribute('open');
      }

      this.askDialogOpenButton?.focus();
    }
  }

  /* ------------------------------------------------------------------------
     Okendo reviews
     ------------------------------------------------------------------------ */

  class ProductCommunityReviews extends HTMLElement {
    constructor() {
      super();
      this.handleFilterClick = this.handleFilterClick.bind(this);
      this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
      this.handleReadMoreClick = this.handleReadMoreClick.bind(this);
      this.handleResize = this.debounce(this.updateReadMoreButtons.bind(this), 200);
    }

    connectedCallback() {
      this.state = {
        reviews: [],
        reviewIds: new Set(),
        nextUrl: null,
        activeTopic: 'all',
        isLoading: false,
        hasStartedLoading: false,
        usingSampleReviews: false,
      };

      this.abortController = new AbortController();
      this.sectionElement = this.closest('product-community-tabs');

      this.reviewList = this.querySelector('[data-review-list]');
      this.statusElement = this.querySelector('[data-review-status]');
      this.emptyElement = this.querySelector('[data-review-empty]');
      this.loadMoreButton = this.querySelector('[data-review-load-more]');
      this.widgetFallbackContainer = this.querySelector('[data-review-widget-fallback]');
      this.cardTemplate = this.querySelector('[data-review-card-template]');
      this.verifiedTemplate = this.querySelector('[data-review-verified-template]');
      this.starsTemplate = this.querySelector('[data-review-stars-template]');
      this.filterButtons = Array.from(this.querySelectorAll('[data-review-topic]'));

      this.settings = {
        productId: this.dataset.productId,
        reviewsPerPage: Number.parseInt(this.dataset.reviewsPerPage, 10) || 6,
        reviewsOrder: this.dataset.reviewsOrder || 'date desc',
        maximumTopicTags: Number.parseInt(this.dataset.maximumTopicTags, 10) || 0,
        showVerifiedBadge: this.dataset.showVerifiedBadge === 'true',
        showReviewTitle: this.dataset.showReviewTitle === 'true',
        showReviewDate: this.dataset.showReviewDate === 'true',
        useWidgetFallback: this.dataset.widgetFallback === 'true',
        needsAggregate: this.dataset.needsAggregate === 'true',
        countLabel: this.dataset.countLabel || '[count] reviews',
        filterEmptyMessage: this.dataset.filterEmptyMessage || '',
        errorMessage: this.dataset.errorMessage || '',
        isDesignMode: this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode),
        locale: this.dataset.locale || document.documentElement.lang || undefined,
      };

      this.topics = this.readTopics();

      this.filterButtons.forEach((filterButton) => filterButton.addEventListener('click', this.handleFilterClick));
      this.loadMoreButton?.addEventListener('click', this.handleLoadMoreClick);
      this.reviewList?.addEventListener('click', this.handleReadMoreClick);
      window.addEventListener('resize', this.handleResize);

      this.observeVisibility();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      this.filterButtons.forEach((filterButton) => filterButton.removeEventListener('click', this.handleFilterClick));
      this.loadMoreButton?.removeEventListener('click', this.handleLoadMoreClick);
      this.reviewList?.removeEventListener('click', this.handleReadMoreClick);
      window.removeEventListener('resize', this.handleResize);
    }

    get subscriberId() {
      const configuredId = (this.dataset.subscriberId || '').trim();
      if (configuredId) return configuredId;

      const okendoMetaTag = document.querySelector('meta[name="oke:subscriber_id"]');
      return (okendoMetaTag?.getAttribute('content') || '').trim();
    }

    /* ---- Lifecycle ---- */

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.startLoading();
        return;
      }

      // Hidden tab panels never intersect, so reviews load only once the panel is shown and near the viewport.
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.visibilityObserver.disconnect();
          this.startLoading();
        },
        { rootMargin: '400px 0px' }
      );
      this.visibilityObserver.observe(this);
    }

    async startLoading() {
      if (this.state.hasStartedLoading) return;
      this.state.hasStartedLoading = true;

      if (!this.subscriberId || !this.settings.productId) {
        if (this.settings.isDesignMode && this.loadSampleReviews()) return;
        this.handleLoadError(new Error('Missing Okendo User ID or product ID.'));
        return;
      }

      if (this.settings.needsAggregate) this.loadAggregate();

      try {
        await this.fetchReviewPage(this.buildInitialReviewsUrl());
        this.render();
        await this.fillActiveFilter();
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.settings.isDesignMode && this.loadSampleReviews()) return;
        this.handleLoadError(error);
      }
    }

    /* ---- Data ---- */

    buildInitialReviewsUrl() {
      const productPath = `/products/shopify-${encodeURIComponent(this.settings.productId)}/reviews`;
      const queryParts = [
        `limit=${encodeURIComponent(this.settings.reviewsPerPage)}`,
        `orderBy=${encodeURIComponent(this.settings.reviewsOrder)}`,
      ];
      return `${this.storeApiBase()}${productPath}?${queryParts.join('&')}`;
    }

    storeApiBase() {
      return `${OKENDO_API_ORIGIN}/stores/${encodeURIComponent(this.subscriberId)}`;
    }

    resolveNextUrl(nextUrl) {
      if (!nextUrl) return null;
      if (/^https?:\/\//i.test(nextUrl)) return nextUrl;
      if (nextUrl.startsWith('/stores/')) return `${OKENDO_API_ORIGIN}${nextUrl}`;
      if (nextUrl.startsWith('/v1/')) return `${OKENDO_API_ORIGIN.replace(/\/v1$/, '')}${nextUrl}`;
      return `${this.storeApiBase()}${nextUrl.startsWith('/') ? '' : '/'}${nextUrl}`;
    }

    async requestOkendo(url) {
      const response = await fetch(url, {
        headers: { 'okendo-api-version': OKENDO_API_VERSION },
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Okendo request failed with status ${response.status}`);
      }

      return response.json();
    }

    async fetchReviewPage(url) {
      if (!url || this.state.isLoading) return;

      this.setLoading(true);
      try {
        const responseData = await this.requestOkendo(url);
        const rawReviews = Array.isArray(responseData?.reviews) ? responseData.reviews : [];

        rawReviews.forEach((rawReview) => {
          const review = this.normalizeReview(rawReview);
          if (this.state.reviewIds.has(review.id)) return;
          this.state.reviewIds.add(review.id);
          this.state.reviews.push(review);
        });

        this.state.nextUrl = this.resolveNextUrl(responseData?.nextUrl);
      } finally {
        this.setLoading(false);
      }
    }

    async loadAggregate() {
      try {
        const aggregateUrl = `${this.storeApiBase()}/products/shopify-${encodeURIComponent(
          this.settings.productId
        )}/review_aggregate`;
        const responseData = await this.requestOkendo(aggregateUrl);
        const aggregate = responseData?.reviewAggregate || responseData || {};
        const reviewCount = Number(aggregate.reviewCount);
        const reviewAverage = Number(aggregate.reviewAverageValue ?? aggregate.reviewAverage);

        if (Number.isFinite(reviewCount) && reviewCount > 0) {
          this.updateSummary(reviewCount, Number.isFinite(reviewAverage) ? reviewAverage : null);
        }
      } catch (error) {
        // The summary is optional; the server-rendered state stays in place.
      }
    }

    loadSampleReviews() {
      const sampleScript = this.querySelector('[data-sample-reviews]');
      if (!sampleScript) return false;

      try {
        const sampleReviews = JSON.parse(sampleScript.textContent);
        this.state.reviews = sampleReviews.map((rawReview) => this.normalizeReview(rawReview));
        this.state.nextUrl = null;
        this.state.usingSampleReviews = true;
        this.render();
        this.showStatus('Theme editor preview: showing sample reviews. Add your Okendo User ID to load live reviews.', {
          isEditorNotice: true,
        });
        return true;
      } catch (error) {
        return false;
      }
    }

    normalizeReview(rawReview = {}) {
      const reviewer = rawReview.reviewer || {};
      const okendoTags = this.extractStrings(rawReview.tags);
      const attributeValues = this.extractAttributeValues(rawReview.productAttributes);
      const searchableText = [rawReview.title, rawReview.body, ...attributeValues, ...okendoTags]
        .filter(Boolean)
        .join(' \n ')
        .toLowerCase();

      const rating = Math.min(5, Math.max(0, Number(rawReview.rating) || 0));

      return {
        id: String(rawReview.reviewId || rawReview.id || `${reviewer.displayName}-${rawReview.dateCreated}-${rawReview.body}`),
        rating,
        title: (rawReview.title || '').trim(),
        body: (rawReview.body || '').trim(),
        author: (reviewer.displayName || reviewer.name || '').trim(),
        isVerified: Boolean(reviewer.isVerified ?? reviewer.isVerifiedBuyer ?? rawReview.isVerified),
        dateCreated: rawReview.dateCreated || null,
        topicHandles: this.matchTopics(searchableText, okendoTags),
      };
    }

    extractStrings(values) {
      if (!Array.isArray(values)) return [];
      return values
        .map((value) => (typeof value === 'string' ? value : value?.name || value?.title || value?.value || ''))
        .map((value) => String(value).trim())
        .filter(Boolean);
    }

    extractAttributeValues(attributes) {
      if (!Array.isArray(attributes)) return [];
      return attributes.flatMap((attribute) => {
        const attributeValue = attribute?.value;
        if (Array.isArray(attributeValue)) return attributeValue.map(String);
        return attributeValue === undefined || attributeValue === null ? [] : [String(attributeValue)];
      });
    }

    /* ---- Topics ---- */

    readTopics() {
      return this.filterButtons
        .filter((filterButton) => filterButton.dataset.reviewTopic !== 'all')
        .map((filterButton) => {
          const label = filterButton.dataset.reviewTopicLabel || filterButton.textContent.trim();
          const keywords = [label, ...(filterButton.dataset.reviewTopicKeywords || '').split(',')]
            .map((keyword) => keyword.trim().toLowerCase())
            .filter(Boolean);

          return {
            handle: filterButton.dataset.reviewTopic,
            label,
            labelLowercase: label.toLowerCase(),
            matchers: [...new Set(keywords)].map(
              (keyword) => new RegExp(`(^|[^\\p{L}\\p{N}])${this.escapeRegularExpression(keyword)}([^\\p{L}\\p{N}]|$)`, 'iu')
            ),
          };
        });
    }

    matchTopics(searchableText, okendoTags) {
      const lowercaseTags = okendoTags.map((tag) => tag.toLowerCase());
      return this.topics
        .filter(
          (topic) =>
            lowercaseTags.includes(topic.labelLowercase) || topic.matchers.some((matcher) => matcher.test(searchableText))
        )
        .map((topic) => topic.handle);
    }

    getVisibleReviews() {
      if (this.state.activeTopic === 'all') return this.state.reviews;
      return this.state.reviews.filter((review) => review.topicHandles.includes(this.state.activeTopic));
    }

    /* ---- Events ---- */

    async handleFilterClick(event) {
      const selectedTopic = event.currentTarget.dataset.reviewTopic;
      if (selectedTopic === this.state.activeTopic) return;

      this.state.activeTopic = selectedTopic;
      this.filterButtons.forEach((filterButton) => {
        filterButton.setAttribute('aria-pressed', String(filterButton.dataset.reviewTopic === selectedTopic));
      });

      this.render();
      try {
        await this.fillActiveFilter();
      } catch (error) {
        if (error.name !== 'AbortError') this.showStatus(this.settings.errorMessage);
      }
    }

    async handleLoadMoreClick() {
      const visibleCountBefore = this.getVisibleReviews().length;
      try {
        let pageRequests = 0;
        do {
          await this.fetchReviewPage(this.state.nextUrl);
          pageRequests += 1;
        } while (
          this.state.nextUrl &&
          this.getVisibleReviews().length === visibleCountBefore &&
          pageRequests < MAXIMUM_AUTOMATIC_PAGE_REQUESTS
        );
        this.render({ preserveScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError') this.showStatus(this.settings.errorMessage);
      }
    }

    handleReadMoreClick(event) {
      const readMoreButton = event.target.closest('[data-review-read-more]');
      if (!readMoreButton) return;

      const reviewBody = readMoreButton.closest('.product-community-review-card')?.querySelector('[data-review-body]');
      if (!reviewBody) return;

      const isExpanded = readMoreButton.getAttribute('aria-expanded') === 'true';
      reviewBody.classList.toggle('is-clamped', isExpanded);
      readMoreButton.setAttribute('aria-expanded', String(!isExpanded));
      readMoreButton.textContent = isExpanded ? readMoreButton.dataset.labelExpand : readMoreButton.dataset.labelCollapse;
    }

    /** When a filter matches too few loaded reviews, fetch a few more pages to fill the grid. */
    async fillActiveFilter() {
      let pageRequests = 0;
      while (
        this.state.activeTopic !== 'all' &&
        this.state.nextUrl &&
        this.getVisibleReviews().length < this.settings.reviewsPerPage &&
        pageRequests < MAXIMUM_AUTOMATIC_PAGE_REQUESTS
      ) {
        await this.fetchReviewPage(this.state.nextUrl);
        pageRequests += 1;
        this.render({ preserveScroll: true });
      }
    }

    /* ---- Rendering ---- */

    render({ preserveScroll = false } = {}) {
      if (!this.reviewList) return;

      const visibleReviews = this.getVisibleReviews();
      const reviewCards = visibleReviews.map((review) => this.createReviewCard(review));
      const previousScrollLeft = this.reviewList.scrollLeft;

      this.reviewList.replaceChildren(...reviewCards);
      this.reviewList.setAttribute('aria-busy', 'false');
      this.reviewList.hidden = visibleReviews.length === 0;

      if (preserveScroll) {
        this.reviewList.scrollLeft = previousScrollLeft;
      } else {
        this.reviewList.scrollLeft = 0;
      }

      const hasAnyReviews = this.state.reviews.length > 0;
      if (this.emptyElement) this.emptyElement.hidden = hasAnyReviews || this.state.isLoading;

      if (hasAnyReviews && visibleReviews.length === 0 && !this.state.isLoading) {
        const activeLabel = this.topics.find((topic) => topic.handle === this.state.activeTopic)?.label || '';
        this.showStatus(this.settings.filterEmptyMessage.replace('[topic]', activeLabel));
      } else if (!this.state.usingSampleReviews) {
        this.hideStatus();
      }

      if (!hasAnyReviews && !this.emptyElement && !this.state.isLoading) {
        this.hideStatus();
      }

      if (this.loadMoreButton) this.loadMoreButton.hidden = !this.state.nextUrl;

      window.requestAnimationFrame(() => this.updateReadMoreButtons());
    }

    createReviewCard(review) {
      const cardFragment = this.cardTemplate.content.cloneNode(true);
      const card = cardFragment.firstElementChild;

      const ratingContainer = card.querySelector('[data-review-rating]');
      const authorElement = card.querySelector('[data-review-author]');
      const dateElement = card.querySelector('[data-review-date]');
      const titleElement = card.querySelector('[data-review-title]');
      const bodyElement = card.querySelector('[data-review-body]');
      const tagList = card.querySelector('[data-review-tags]');

      if (ratingContainer && this.starsTemplate) {
        const starsFragment = this.starsTemplate.content.cloneNode(true);
        const starsElement = starsFragment.querySelector('[data-star-rating]');
        if (starsElement) {
          starsElement.style.setProperty('--product-community-stars-fill', `${(review.rating / 5) * 100}%`);
          starsElement.setAttribute('aria-label', `${review.rating} out of 5 stars`);
        }
        ratingContainer.append(starsFragment);
      }

      if (authorElement) authorElement.textContent = review.author;

      if (dateElement && this.settings.showReviewDate && review.dateCreated) {
        const reviewDate = new Date(review.dateCreated);
        if (!Number.isNaN(reviewDate.getTime())) {
          dateElement.textContent = reviewDate.toLocaleDateString(this.settings.locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
          dateElement.hidden = false;
        }
      }

      if (titleElement && this.settings.showReviewTitle && review.title) {
        titleElement.textContent = review.title;
        titleElement.hidden = false;
      }

      if (bodyElement) {
        bodyElement.textContent = review.body;
        if (this.lineClampValue() > 0) bodyElement.classList.add('is-clamped');
      }

      if (tagList) {
        if (this.settings.showVerifiedBadge && review.isVerified && this.verifiedTemplate) {
          tagList.append(this.verifiedTemplate.content.cloneNode(true));
        }

        review.topicHandles.slice(0, this.settings.maximumTopicTags).forEach((topicHandle) => {
          const topic = this.topics.find((candidateTopic) => candidateTopic.handle === topicHandle);
          if (!topic) return;
          const tagItem = document.createElement('li');
          tagItem.className = 'product-community-review-card__tag';
          tagItem.textContent = topic.label;
          tagList.append(tagItem);
        });
      }

      return card;
    }

    lineClampValue() {
      const clampValue = getComputedStyle(this).getPropertyValue('--product-community-review-line-clamp');
      return Number.parseInt(clampValue, 10) || 0;
    }

    updateReadMoreButtons() {
      if (!this.reviewList || this.lineClampValue() === 0) return;

      this.reviewList.querySelectorAll('.product-community-review-card').forEach((card) => {
        const reviewBody = card.querySelector('[data-review-body]');
        const readMoreButton = card.querySelector('[data-review-read-more]');
        if (!reviewBody || !readMoreButton) return;
        if (readMoreButton.getAttribute('aria-expanded') === 'true') return;

        const isOverflowing = reviewBody.scrollHeight > reviewBody.clientHeight + 1;
        readMoreButton.hidden = !isOverflowing;
      });
    }

    updateSummary(reviewCount, reviewAverage) {
      const sectionElement = this.sectionElement || document;
      const tabCount = sectionElement.querySelector('[data-reviews-tab-count]');
      if (tabCount) {
        tabCount.textContent = `(${reviewCount})`;
        tabCount.hidden = false;
      }

      const summaryCount = sectionElement.querySelector('[data-rating-summary-count]');
      if (summaryCount) summaryCount.textContent = this.settings.countLabel.replace('[count]', reviewCount);

      const summaryValue = sectionElement.querySelector('[data-rating-summary-value]');
      if (summaryValue && reviewAverage !== null) summaryValue.textContent = reviewAverage.toFixed(1);

      const summaryStars = sectionElement.querySelector('[data-rating-summary] [data-star-rating]');
      if (summaryStars && reviewAverage !== null) {
        summaryStars.style.setProperty('--product-community-stars-fill', `${(reviewAverage / 5) * 100}%`);
        summaryStars.setAttribute('aria-label', `${reviewAverage.toFixed(1)} out of 5 stars`);
      }
    }

    /* ---- States ---- */

    setLoading(isLoading) {
      this.state.isLoading = isLoading;
      this.reviewList?.setAttribute('aria-busy', String(isLoading));
      if (this.loadMoreButton) {
        this.loadMoreButton.disabled = isLoading;
        this.loadMoreButton.setAttribute('aria-busy', String(isLoading));
      }
    }

    showStatus(message, { isEditorNotice = false } = {}) {
      if (!this.statusElement || !message) return;
      this.statusElement.textContent = message;
      this.statusElement.classList.toggle('product-community-reviews__status--editor-notice', isEditorNotice);
      this.statusElement.hidden = false;
    }

    hideStatus() {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      this.statusElement.hidden = true;
    }

    handleLoadError(error) {
      if (this.reviewList) {
        this.reviewList.replaceChildren();
        this.reviewList.hidden = true;
        this.reviewList.setAttribute('aria-busy', 'false');
      }
      if (this.loadMoreButton) this.loadMoreButton.hidden = true;

      const filtersElement = this.querySelector('[data-review-filters]');

      if (this.settings.useWidgetFallback && this.mountOkendoWidget()) {
        if (filtersElement) filtersElement.hidden = true;
        return;
      }

      this.showStatus(this.settings.errorMessage);
      if (window.console && error) console.warn('[product-community] Reviews could not be loaded:', error);
    }

    mountOkendoWidget() {
      const widgetTemplate = this.querySelector('[data-okendo-widget-template]');
      if (!widgetTemplate || !this.widgetFallbackContainer) return false;

      this.widgetFallbackContainer.replaceChildren(widgetTemplate.content.cloneNode(true));
      this.widgetFallbackContainer.hidden = false;

      const widgetElement = this.widgetFallbackContainer.querySelector('[data-oke-widget]');
      const initializeWidget = () => {
        if (widgetElement && typeof window.okeWidgetApi?.initWidget === 'function') {
          window.okeWidgetApi.initWidget(widgetElement);
          return true;
        }
        return false;
      };

      if (!initializeWidget()) {
        // Widget Plus may still be loading; it also scans the page for [data-oke-widget] on its own.
        document.addEventListener('oke-script-loaded', initializeWidget, { once: true });
      }

      return true;
    }

    /* ---- Utilities ---- */

    escapeRegularExpression(value) {
      return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    debounce(callback, waitInMilliseconds) {
      let timeoutId;
      return (...callbackArguments) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => callback(...callbackArguments), waitInMilliseconds);
      };
    }
  }

  if (!customElements.get('product-community-tabs')) {
    customElements.define('product-community-tabs', ProductCommunityTabs);
  }

  if (!customElements.get('product-community-reviews')) {
    customElements.define('product-community-reviews', ProductCommunityReviews);
  }
})();
